'use client';

import { endOfMonth, format, getMonth, getYear, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Flag from 'react-world-flags';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { Skeleton } from '../../../components/ui/skeleton';
import { getCountryCode, getDeclinedMonthName } from '../lib/utils';
import { CurrencyChartMonth } from './detail-page/CurrencyChartMonth';
import { CurrencySummaryCard } from './detail-page/CurrencySummaryCard';
import { DailyRatesList } from './detail-page/DailyRatesList';
import { MonthlyHistoryTable } from './detail-page/MonthlyHistoryTable';

// ... (Интерфейсы оставляем те же, я их скрыл для краткости) ...
interface ApiRate {
  date: string;
  fullDate: string;
  charCode: string;
  nominal: number;
  name: string;
  value: number;
}

interface ApiResponseData {
  rates: ApiRate[];
  yesterdayRate?: { value: number } | null;
  monthlySummaries?: MonthlySummaryData[];
  lastUpdated?: string | Date;
}

interface DailyCurrencyData {
  date: string;
  fullDate: Date;
  value: number;
  change?: number;
}

interface SummaryData {
  currencyName: string;
  startOfMonthRate: number | null;
  todayRate: number | null;
  todayChange: number | null;
  minMonthRate: number | null;
  maxMonthRate: number | null;
  latestUpdate: Date | null;
}

interface MonthlySummaryData {
  month: number;
  year: number;
  monthName: string;
  averageRate: number | null;
  startOfMonthRate: number | null;
  endOfMonthRate: number | null;
  minRate: number | null;
  maxRate: number | null;
}

interface CurrencyDetailClientProps {
  charCode: string;
  initialData: ApiResponseData | null;
  initialError: string | null;
  selectedMonthInitial?: number;
  selectedYearInitial?: number;
}

export function CurrencyDetailClient({
  charCode,
  initialData,
  initialError,
  selectedMonthInitial,
  selectedYearInitial,
}: CurrencyDetailClientProps) {
  // Флаг, чтобы эффект инициализации сработал строго 1 раз
  const [isInitialized, setIsInitialized] = useState(false);

  const [loading, setLoading] = useState(!initialData && !initialError);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [chartData, setChartData] = useState<DailyCurrencyData[]>([]);
  const [listData, setListData] = useState<DailyCurrencyData[]>([]);
  const [error, setError] = useState<string | null>(initialError);
  const [allMonthlySummaries, setAllMonthlySummaries] = useState<MonthlySummaryData[]>([]);
  const monthCacheRef = useRef(new Map<string, ApiResponseData>());
  const abortRef = useRef<AbortController | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const today = useMemo(() => new Date(), []);

  const [currentDisplayMonth, setCurrentDisplayMonth] = useState<number>(
    selectedMonthInitial ?? getMonth(today),
  );
  const [currentDisplayYear, setCurrentDisplayYear] = useState<number>(
    selectedYearInitial ?? getYear(today),
  );

  const processFetchedData = useCallback(
    (data: ApiResponseData, targetMonth: number, targetYear: number) => {
      if (!data || !data.rates) {
        console.error('[Client] processFetchedData: Invalid data received.', data);
        setError(`Некорректные данные получены для ${charCode.toUpperCase()}.`);
        setLoading(false);
        setIsUpdating(false);
        return;
      }

      // Ослабляем фильтр будущего: просто берем всё, что не из будущего по году/месяцу
      const isCurrentMonthView = targetYear === getYear(today) && targetMonth === getMonth(today);
      const filterEndDate = isCurrentMonthView
        ? today
        : endOfMonth(new Date(targetYear, targetMonth, 1));

      const filteredDailyRates = data.rates
        .map((rate: ApiRate) => ({
          ...rate,
          fullDate: rate.fullDate ? parseISO(rate.fullDate) : new Date(NaN),
        }))
        .filter((rate) => rate.fullDate && !isNaN(rate.fullDate.getTime()))
        // Убрали жесткую проверку getMonth === targetMonth, чтобы избежать проблем часовых поясов
        .filter((rate) => rate.fullDate <= filterEndDate)
        .sort(
          (a: { fullDate: Date }, b: { fullDate: Date }) =>
            a.fullDate.getTime() - b.fullDate.getTime(),
        );

      if (filteredDailyRates.length === 0) {
        setError(
          `Нет данных за ${format(new Date(targetYear, targetMonth, 1), 'LLLL yyyy', { locale: ru })} для ${charCode.toUpperCase()}.`,
        );

        setLoading(false);
        setIsUpdating(false);
        return;
      } else {
        setError(null);
      }

      const ratesForProcessing = filteredDailyRates as unknown as DailyCurrencyData[];

      const startOfMonthRate = ratesForProcessing[0]?.value ?? null;
      const todayRate = ratesForProcessing[ratesForProcessing.length - 1]?.value ?? null;
      const values = ratesForProcessing.map((r) => r.value);
      const minMonthRate = values.length > 0 ? Math.min(...values) : null;
      const maxMonthRate = values.length > 0 ? Math.max(...values) : null;
      const yesterdayRateValue = data.yesterdayRate?.value;
      const todayChange =
        todayRate !== null && yesterdayRateValue !== undefined && yesterdayRateValue !== null
          ? todayRate - yesterdayRateValue
          : null;

      const currencyName = filteredDailyRates[0]?.name || charCode.toUpperCase();

      const newSummary: SummaryData = {
        currencyName,
        startOfMonthRate,
        todayRate,
        todayChange,
        minMonthRate,
        maxMonthRate,
        latestUpdate: data.lastUpdated ? new Date(data.lastUpdated) : null,
      };
      setSummary(newSummary);

      const preparedChartData: DailyCurrencyData[] = ratesForProcessing.map((rate, index) => {
        const prevDayRateObj = index > 0 ? ratesForProcessing[index - 1] : null;
        const dailyChange = prevDayRateObj ? rate.value - prevDayRateObj.value : undefined;
        return {
          date: format(rate.fullDate, 'dd.MM', { locale: ru }),
          fullDate: rate.fullDate,
          value: rate.value,
          change: dailyChange,
        };
      });
      setChartData(preparedChartData);
      setListData([...preparedChartData].reverse());
      setAllMonthlySummaries(data.monthlySummaries || []);
      setLoading(false);
      setIsUpdating(false);
    },
    [charCode, today],
  );

  // --- 🔥 ИСПРАВЛЕННЫЙ EFFECT (Убраны лишние зависимости) ---
  useEffect(() => {
    // Если уже инициализировали - выходим. Это предотвратит повторный сброс.
    if (isInitialized) return;

    if (initialError) {
      setError(initialError);
      setLoading(false);
      setIsInitialized(true);
      return;
    }

    if (initialData) {
      console.log('[Client] Processing INITIAL data...');
      // Обрабатываем данные только 1 раз при старте
      processFetchedData(initialData, currentDisplayMonth, currentDisplayYear);
      setIsInitialized(true);
    } else {
      setLoading(false);
      setIsInitialized(true);
    }
    // ✅ Оставляем только пустой массив зависимостей (или минимум), чтобы сработало 1 раз
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Фоновое обновление истории
  // useEffect(() => {
  //   const triggerCollection = async () => {
  //     try {
  //       await fetch('/api/finance/collect-historical-rates');
  //     } catch (err) {
  //       console.error('[Client] Error triggering collection API:', err);
  //     }
  //   };
  //   triggerCollection();
  // }, []);

  const handleMonthClick = useCallback(
    async (year: number, month: number) => {
      if (currentDisplayYear === year && currentDisplayMonth === month) return;

      const key = `${charCode.toUpperCase()}_${year}_${month}`;

      // 1) если есть в локальном кэше — не ходим в сеть
      const cached = monthCacheRef.current.get(key);
      if (cached) {
        setIsUpdating(true);
        setError(null);

        processFetchedData(cached, month, year);
        setCurrentDisplayMonth(month);
        setCurrentDisplayYear(year);
        return;
      }

      // 2) отменяем предыдущий запрос
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsUpdating(true);
      setError(null);

      try {
        const res = await fetch(
          `/api/finance/currency-history/${charCode.toUpperCase()}?year=${year}&month=${month}&includeSummaries=1`,
          { signal: controller.signal },
        );

        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          throw new Error(errorData.error || `Не удалось загрузить данные.`);
        }

        const data: ApiResponseData = await res.json();

        // 3) сохраняем в локальный кэш
        monthCacheRef.current.set(key, data);

        processFetchedData(data, month, year);
        setCurrentDisplayMonth(month);
        setCurrentDisplayYear(year);
      } catch (err: unknown) {
        if ((err as any)?.name === 'AbortError') {
          setIsUpdating(false);
          return; // нормальная отмена
        }

        console.error('[Client] Ошибка при смене месяца:', err);
        setError(err instanceof Error ? err.message : 'Ошибка загрузки.');
        setIsUpdating(false);
      }
    },
    [currentDisplayYear, currentDisplayMonth, charCode, processFetchedData],
  );

  const currencyActualName = useMemo(
    () => summary?.currencyName || charCode.toUpperCase(),
    [summary, charCode],
  );

  const declinedMonthName = useMemo(
    () => getDeclinedMonthName(currentDisplayMonth, 'prepositional'),
    [currentDisplayMonth],
  );

  const isInitialLoading = loading && !summary;
  const isMonthlyTableLoading = isInitialLoading && allMonthlySummaries.length === 0;
  const updatingClassName = isUpdating ? 'opacity-70' : 'opacity-100';

  return (
    <div className="flex flex-col gap-6 md:gap-8 text-main">
      <Breadcrumbs />
      <h1 className="text-xl md:text-2xl lg:text-3xl font-bold flex items-center gap-3">
        {isInitialLoading ? (
          <Skeleton className="w-9 h-9 rounded-full" />
        ) : (
          <Flag
            code={getCountryCode(charCode)}
            height={36}
            width={36}
            className="rounded-full border border-gray-600 object-cover shrink-0"
            fallback={<span className="inline-block w-9 h-9 bg-gray-700 rounded-full" />}
          />
        )}
        {isInitialLoading ? (
          <Skeleton className="w-3/4 h-8" />
        ) : (
          `Курс ${currencyActualName} (${charCode.toUpperCase()}) в ${declinedMonthName} ${currentDisplayYear} года`
        )}
      </h1>

      {error && !loading && (
        <div className="p-4 bg-red-900/30 border border-red-500/50 rounded-lg text-red-300 text-center">
          {error}
        </div>
      )}

      <CurrencySummaryCard summary={summary} loading={isInitialLoading} />

      <div
        className={`flex flex-col xl:flex-row gap-6 md:gap-8 ${updatingClassName}`}
        aria-busy={isUpdating}
      >
        <CurrencyChartMonth
          chartData={chartData}
          loading={isInitialLoading}
          currencyName={currencyActualName}
          monthName={getDeclinedMonthName(currentDisplayMonth, 'nominative')}
          year={currentDisplayYear}
        />

        <DailyRatesList
          listData={listData}
          loading={isInitialLoading}
          currencyName={currencyActualName}
          monthName={getDeclinedMonthName(currentDisplayMonth, 'nominative')}
        />
      </div>

      <MonthlyHistoryTable
        monthlySummaries={allMonthlySummaries}
        loading={loading}
        charCode={charCode}
        currencyName={currencyActualName}
        currentYear={getYear(today)}
        onMonthClick={handleMonthClick}
        selectedYear={currentDisplayYear}
        selectedMonth={currentDisplayMonth}
      />
    </div>
  );
}
